import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Loader2, RefreshCw, Server, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { clientRequestHeaders } from "@/lib/sm-api";

type PlatformStatus = {
  generatedAt: string;
  runtime: { nodeEnv: string; sessionTransport: string };
  integrations: {
    upstash: { configured: boolean; detail: string };
    sentry: { configured: boolean; detail: string };
    cron: {
      secretConfigured: boolean;
      alertEmailConfigured?: boolean;
      alertEmail?: string;
      alertEmailSource?: "env" | "default";
      resendConfigured?: boolean;
      runsRetentionDays?: number;
      jobs: {
        id: string;
        fn?: string;
        path?: string;
        schedule: string;
        lastStatus?: string | null;
        lastRunAt?: string | null;
        lastMessage?: string | null;
      }[];
    };
  };
  customDomains?: { byStatus: Record<string, number>; total: number };
};

type CronRun = {
  id: string;
  jobId: string;
  status: string;
  message: string | null;
  executedAt?: string;
  createdAt?: string;
  payload?: Record<string, unknown> | null;
};

function statusIcon(status: string) {
  if (status === "success") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
  if (status === "auth_failed") return <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />;
  return <AlertTriangle className="h-3.5 w-3.5 text-destructive" />;
}

function statusLabel(status: string) {
  if (status === "success") return "OK";
  if (status === "auth_failed") return "Auth";
  return "Error";
}

function runAt(r: CronRun) {
  return r.executedAt ?? r.createdAt ?? "";
}

export function PlatformStatusPanel() {
  const [data, setData] = useState<PlatformStatus | null>(null);
  const [cronRuns, setCronRuns] = useState<CronRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const headers = clientRequestHeaders();
    try {
      const [statusRes, runsRes] = await Promise.all([
        fetch("/api/admin/platform-status", { credentials: "include", headers }),
        fetch("/api/admin/cron-runs?limit=30", { credentials: "include", headers }),
      ]);
      const statusBody = (await statusRes.json()) as PlatformStatus & { error?: string };
      const runsBody = (await runsRes.json()) as { runs?: CronRun[]; error?: string };
      if (!statusRes.ok) {
        setError(statusBody.error ?? "No autorizado");
        setData(null);
        setCronRuns([]);
        return;
      }
      setData(statusBody);
      setCronRuns(Array.isArray(runsBody.runs) ? runsBody.runs : []);
    } catch {
      setError("Error de red");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="glass-card flex items-center gap-2 rounded-xl p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando estado de plataforma…
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-xl border border-destructive/30 p-4 text-sm text-destructive">{error}</div>
    );
  }

  if (!data) return null;

  const statusBadge = (ok: boolean) => (
    <Badge variant={ok ? "secondary" : "destructive"} className="text-[10px]">
      {ok ? "OK" : "Falta config"}
    </Badge>
  );

  const recentFailures = cronRuns.filter((r) => r.status !== "success").slice(0, 5);
  const customDomains = data.customDomains ?? { byStatus: {}, total: 0 };

  return (
    <div className="glass-card space-y-4 rounded-xl p-4" data-testid="platform-status-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">Estado de plataforma</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Actualizar
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-border/60 bg-secondary/15 p-3">
          <p className="text-xs text-muted-foreground">Upstash (rate limit)</p>
          <div className="mt-1 flex items-center gap-2">
            {statusBadge(data.integrations.upstash.configured)}
            <span className="text-xs">{data.integrations.upstash.detail}</span>
          </div>
        </div>
        <div className="rounded-lg border border-border/60 bg-secondary/15 p-3">
          <p className="text-xs text-muted-foreground">Crons</p>
          <div className="mt-1 flex items-center gap-2">
            {statusBadge(data.integrations.cron.secretConfigured)}
            <span className="text-xs">{data.integrations.cron.jobs.length} jobs programados</span>
          </div>
          {!data.integrations.cron.secretConfigured ? (
            <span className="mt-1 block text-[10px] text-amber-500">
              CRON_SECRET no configurado en Netlify (scheduled functions no pueden autenticarse)
            </span>
          ) : null}
          {data.integrations.cron.alertEmailConfigured ? (
            <span className="mt-1 block text-[10px] text-muted-foreground">
              Alertas → {data.integrations.cron.alertEmail}
              {data.integrations.cron.alertEmailSource === "default" ? " (default)" : ""}
            </span>
          ) : data.integrations.cron.resendConfigured === false ? (
            <span className="mt-1 block text-[10px] text-amber-500">
              Resend sin API key (env o tab Correo y Resend)
            </span>
          ) : (
            <span className="mt-1 block text-[10px] text-amber-500">Alertas email sin destinatario</span>
          )}
        </div>
        <div className="rounded-lg border border-border/60 bg-secondary/15 p-3">
          <p className="text-xs text-muted-foreground">Sentry</p>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={data.integrations.sentry.configured ? "secondary" : "outline"} className="text-[10px]">
              {data.integrations.sentry.configured ? "Activo" : "Opcional"}
            </Badge>
            <span className="text-xs">{data.integrations.sentry.detail}</span>
          </div>
        </div>
        <div className="rounded-lg border border-border/60 bg-secondary/15 p-3">
          <p className="text-xs text-muted-foreground">Sesión</p>
          <p className="mt-1 text-sm font-medium">{data.runtime.sessionTransport}</p>
          <p className="text-[10px] text-muted-foreground">{data.runtime.nodeEnv}</p>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Activity className="h-3.5 w-3.5" />
            Dominios custom ({customDomains.total})
          </p>
          <ul className="space-y-1 text-sm">
            {Object.entries(customDomains.byStatus).length ? (
              Object.entries(customDomains.byStatus).map(([status, n]) => (
                <li key={status} className="flex justify-between gap-2">
                  <span className="text-muted-foreground">{status}</span>
                  <span className="font-medium">{n}</span>
                </li>
              ))
            ) : (
              <li className="text-xs text-muted-foreground">Ningún dominio custom configurado.</li>
            )}
          </ul>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Jobs programados</p>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground">
            {data.integrations.cron.jobs.map((j) => (
              <li key={j.id} className="flex justify-between gap-2 rounded-md border border-border/40 px-2 py-1">
                <code className="truncate text-foreground">{j.id}</code>
                <span className="shrink-0">{j.schedule}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div data-testid="cron-runs-history">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Historial cron (reciente)
        </p>
        {cronRuns.length ? (
          <>
            {recentFailures.length > 0 && (
              <div className="mb-3 rounded-lg border border-destructive/30 bg-destructive/5 p-2 text-xs">
                <p className="font-medium text-destructive">{recentFailures.length} fallo(s) reciente(s)</p>
                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                  {recentFailures.map((r) => (
                    <li key={r.id}>
                      {r.jobId}: {r.message ?? statusLabel(r.status)} ·{" "}
                      {runAt(r) ? new Date(runAt(r)).toLocaleString("es-AR") : "—"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <ul className="max-h-52 space-y-1 overflow-y-auto text-xs">
              {cronRuns.map((r) => (
                <li
                  key={r.id}
                  className="flex items-start justify-between gap-2 rounded-md border border-border/40 px-2 py-1.5"
                >
                  <div className="flex min-w-0 items-start gap-1.5">
                    {statusIcon(r.status)}
                    <div className="min-w-0">
                      <code className="text-foreground">{r.jobId}</code>
                      {r.message && r.status !== "success" ? (
                        <p className="truncate text-[10px] text-destructive">{r.message}</p>
                      ) : null}
                    </div>
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {runAt(r)
                      ? new Date(runAt(r)).toLocaleString("es-AR", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                      : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            Sin ejecuciones registradas. Esperá la próxima corrida cron o revisá la tabla{" "}
            <code>sm_cron_runs</code>.
          </p>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground">
        Generado {new Date(data.generatedAt).toLocaleString("es-AR")}
      </p>
    </div>
  );
}
